// src/api/assistant.ts
// Data access for the global platform assistant (plan 2026-07-28, step 3.1).
//
// Assistant conversations are workspace-agnostic and private to their creator;
// the backend filters by account_id + user_id + surface='assistant'. The BFF
// forwards the ?surface= query param explicitly on the list route.
import { apiJson } from "./client";

export interface AssistantConversation {
  id: string;
  title: string | null;
  updatedAt: string | null;
}

/** Recent assistant conversations for the current user (newest first). */
export async function listAssistantConversations(): Promise<AssistantConversation[]> {
  const rows = await apiJson<any[]>("/chats/conversations?surface=assistant");
  if (!Array.isArray(rows)) return [];
  return rows.map((c) => ({
    id: c.id,
    title: c.title ?? null,
    updatedAt: c.updated_at ?? null,
  }));
}

/**
 * Load the most recent `limit` messages of a conversation (ascending order —
 * the backend returns newest-first then reverses per page). Reuses the shared
 * mapper at the call site.
 */
export async function loadAssistantMessages(
  conversationId: string,
  limit = 50,
): Promise<any[]> {
  const params = new URLSearchParams({ limit: String(limit), offset: "0" });
  const rows = await apiJson<any[]>(
    `/chats/conversations/${encodeURIComponent(conversationId)}/messages?${params}`,
  );
  return Array.isArray(rows) ? rows : [];
}

/** Persist a multiple-choice selection (fire-and-forget at the call site). */
export async function persistMcSelection(
  messageId: string,
  selectedOption: string,
): Promise<void> {
  await apiJson(
    `/chats/messages/${encodeURIComponent(messageId)}/mc-selection`,
    { method: "PATCH", body: JSON.stringify({ selected_option: selectedOption }) },
  );
}
